import React, { useEffect, useMemo, useState } from "react";
import { AppLayout } from "../components/app/AppLayout";
import { reportsApi } from "../lib/api";
import { toast } from "sonner";
import {
  Download, Loader2, BarChart3, TrendingDown, TrendingUp, ChevronDown, Activity,
} from "lucide-react";
import { TID } from "../constants/testIds";
import { getApiError } from "../lib/utils";

const fmtDate = (iso) => (iso ? iso.slice(0, 10) : "");
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const KPI = ({ label, value, tone = "default", hint }) => {
  const tones = {
    default: "border-slate-200 bg-white",
    good: "border-emerald-200 bg-emerald-50",
    warn: "border-amber-200 bg-amber-50",
    bad: "border-red-200 bg-red-50",
    info: "border-nmi-200 bg-nmi-50",
  };
  return (
    <div className={`rounded-md border ${tones[tone]} p-4`}>
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
      <div className="mt-1 font-heading text-2xl md:text-3xl font-bold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-[10px] text-slate-500">{hint}</div>}
    </div>
  );
};

// Simple SVG bar chart for trend series
const TrendChart = ({ series }) => {
  const maxVal = Math.max(1, ...series.map((s) => Math.max(s.suspensions, s.reactivations)));
  const barW = Math.max(12, Math.floor(600 / Math.max(1, series.length)) - 8);
  return (
    <div className="overflow-x-auto rounded-md border border-slate-200 bg-white p-4">
      {series.length === 0 ? (
        <div className="p-6 text-center text-sm text-slate-500">No events in the selected window.</div>
      ) : (
        <svg width={Math.max(600, series.length * (barW + 8) + 40)} height="240" className="min-w-full">
          {series.map((s, i) => {
            const sh = Math.round((s.suspensions / maxVal) * 180);
            const rh = Math.round((s.reactivations / maxVal) * 180);
            const x = 20 + i * (barW + 8);
            return (
              <g key={s.period}>
                <rect x={x} y={200 - sh} width={barW / 2} height={sh} fill="#dc2626" rx="2" />
                <rect x={x + barW / 2 + 2} y={200 - rh} width={barW / 2} height={rh} fill="#059669" rx="2" />
                <text x={x + barW / 2} y={218} fontSize="9" textAnchor="middle" fill="#64748b">
                  {s.period.slice(-5)}
                </text>
                {s.suspensions > 0 && (
                  <text x={x + barW / 4} y={200 - sh - 4} fontSize="9" textAnchor="middle" fill="#dc2626" fontWeight="700">
                    {s.suspensions}
                  </text>
                )}
                {s.reactivations > 0 && (
                  <text x={x + 3 * barW / 4} y={200 - rh - 4} fontSize="9" textAnchor="middle" fill="#059669" fontWeight="700">
                    {s.reactivations}
                  </text>
                )}
              </g>
            );
          })}
          <line x1="20" y1="200" x2={Math.max(600, series.length * (barW + 8) + 20)} y2="200" stroke="#e2e8f0" />
        </svg>
      )}
      <div className="mt-3 flex gap-4 text-[11px] text-slate-600">
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-red-600 inline-block" /> Suspensions</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-sm bg-emerald-600 inline-block" /> Reactivations</span>
      </div>
    </div>
  );
};

export default function Reports() {
  const [from, setFrom] = useState(daysAgoIso(90));
  const [to, setTo] = useState(todayIso());
  const [bucket, setBucket] = useState("week");
  const [netStatus, setNetStatus] = useState(null);
  const [trend, setTrend] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(false);

  const params = useMemo(() => ({ from, to }), [from, to]);

  const load = async () => {
    setLoading(true);
    try {
      const [ns, tr, tk] = await Promise.all([
        reportsApi.networkStatus(params),
        reportsApi.trend({ ...params, bucket }),
        reportsApi.suspensionTracking(params),
      ]);
      setNetStatus(ns);
      setTrend(tr);
      setTracking(tk);
    } catch (e) {
      toast.error(getApiError(e, "Failed to load reports"));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, to, bucket]);

  const [showBreakdown, setShowBreakdown] = useState(false);

  return (
    <AppLayout mobileTitle="Reports">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            Analytics
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
            Reports
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Suspension tracking, network availability and trend analysis.
          </p>
        </div>
        <a
          data-testid={TID.reportDownloadCsv}
          href={reportsApi.csvUrl({ from, to })}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-1.5 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
        >
          <Download size={14} strokeWidth={2.5} /> Download CSV
        </a>
      </div>

      {/* Range picker */}
      <div className="mb-6 flex flex-wrap items-end gap-3 rounded-md border border-slate-200 bg-slate-50 p-3">
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">From</label>
          <input
            data-testid={TID.reportRangeFrom}
            type="date"
            value={from}
            max={to}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-nmi-600"
          />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">To</label>
          <input
            data-testid={TID.reportRangeTo}
            type="date"
            value={to}
            min={from}
            max={todayIso()}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-nmi-600"
          />
        </div>
        <div>
          <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Bucket</label>
          <select
            data-testid={TID.reportBucket}
            value={bucket}
            onChange={(e) => setBucket(e.target.value)}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-nmi-600"
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </div>
        <div className="ml-auto flex gap-1.5">
          {[
            ["7d", 7], ["30d", 30], ["90d", 90], ["1y", 365],
          ].map(([label, n]) => (
            <button
              key={label}
              onClick={() => { setFrom(daysAgoIso(n)); setTo(todayIso()); }}
              className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 hover:bg-slate-100"
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {loading && !netStatus ? (
        <div className="rounded-md border border-slate-200 bg-white p-10 text-center text-slate-500">
          <Loader2 size={20} className="inline animate-spin" /> Loading reports…
        </div>
      ) : (
        <>
          {/* KPI grid */}
          {netStatus && (
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <KPI label="Total providers" value={netStatus.total.toLocaleString()} />
              <KPI label="Active now" value={netStatus.active_now.toLocaleString()} tone="good" hint={`${netStatus.active_pct}% of network`} />
              <KPI label="Suspended now" value={netStatus.suspended_now.toLocaleString()} tone="bad" />
              <KPI
                label="Avg active % (window)"
                value={netStatus.window ? `${netStatus.window.avg_active_pct}%` : "—"}
                tone="info"
                hint={netStatus.window ? `Δt = ${Math.round((new Date(netStatus.window.to) - new Date(netStatus.window.from)) / 86400000)} days` : undefined}
              />
            </div>
          )}

          {tracking && (
            <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
              <KPI label="Total events" value={tracking.total_events} />
              <KPI label="Suspensions" value={tracking.suspensions} tone="bad" />
              <KPI label="Reactivations" value={tracking.reactivations} tone="good" />
              <KPI label="Still suspended" value={tracking.still_suspended} tone="warn" />
            </div>
          )}

          {/* Trend chart */}
          <div className="mt-8">
            <div className="mb-2 flex items-center gap-2">
              <BarChart3 size={16} className="text-slate-700" />
              <h2 className="font-heading text-lg font-bold tracking-tight text-slate-900">
                Trend — status changes over time
              </h2>
            </div>
            {trend && <TrendChart series={trend.series} />}
          </div>

          {/* Breakdown */}
          {netStatus && (
            <div className="mt-8">
              <button
                onClick={() => setShowBreakdown((v) => !v)}
                className="flex w-full items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-4 py-3 text-left hover:bg-slate-100"
              >
                <div className="flex items-center gap-2">
                  <Activity size={15} className="text-slate-700" />
                  <span className="font-heading text-sm font-bold uppercase tracking-wider text-slate-900">
                    Breakdown by category & region
                  </span>
                </div>
                <ChevronDown size={16} className={`transition-transform ${showBreakdown ? "rotate-180" : ""}`} />
              </button>
              {showBreakdown && (
                <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                  <BreakdownTable title="By facility type" rows={netStatus.by_category} labelKey="category" />
                  <BreakdownTable title="By region" rows={netStatus.by_region} labelKey="region" />
                </div>
              )}
            </div>
          )}

          {/* Recent events */}
          {tracking && (
            <div className="mt-8">
              <h2 className="mb-3 font-heading text-lg font-bold tracking-tight text-slate-900">
                Suspension tracking — {tracking.items.length} events
              </h2>
              <div className="overflow-x-auto rounded-md border border-slate-200">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">When</th>
                      <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Facility</th>
                      <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Region</th>
                      <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Change</th>
                      <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Duration</th>
                      <th className="px-3 py-2 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tracking.items.slice(0, 100).map((e) => (
                      <tr key={e.audit_id} className="border-t border-slate-200">
                        <td className="px-3 py-2 font-mono text-[11px] text-slate-600 whitespace-nowrap">{e.timestamp?.slice(0, 16).replace("T", " ")}</td>
                        <td className="px-3 py-2 text-slate-900">{e.facility_name}</td>
                        <td className="px-3 py-2 text-slate-700">{e.region || "—"}</td>
                        <td className="px-3 py-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${e.new_status === "SUSPENDED" ? "text-red-700" : "text-emerald-700"}`}>
                            {e.old_status} → {e.new_status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-700">
                          {e.duration_hours != null ? `${e.duration_hours} h` : e.new_status === "SUSPENDED" ? <span className="text-red-700">ongoing</span> : "—"}
                        </td>
                        <td className="px-3 py-2 text-xs text-slate-700">{e.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {tracking.items.length > 100 && (
                <p className="mt-2 text-[11px] text-slate-500">Showing 100 of {tracking.items.length}. Download the CSV for the full dataset.</p>
              )}
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
}

const BreakdownTable = ({ title, rows, labelKey }) => (
  <div className="rounded-md border border-slate-200">
    <div className="border-b border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-slate-600">{title}</div>
    <div className="max-h-72 overflow-y-auto">
      <table className="w-full text-sm">
        <tbody>
          {rows.map((r) => {
            const pct = r.total ? (100 * r.active) / r.total : 100;
            const suspended = r.suspended || 0;
            return (
              <tr key={r[labelKey] || "—"} className="border-t border-slate-100">
                <td className="px-3 py-2 text-xs font-medium text-slate-900">{r[labelKey] || "—"}</td>
                <td className="px-3 py-2 text-right font-mono text-[11px] text-slate-700">{r.total}</td>
                <td className="px-3 py-2 w-40">
                  <div className="flex items-center gap-2">
                    <div className="h-2 flex-1 rounded-full bg-slate-100">
                      <div
                        className={`h-2 rounded-full ${pct >= 95 ? "bg-emerald-500" : pct >= 80 ? "bg-amber-500" : "bg-red-500"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className={`w-10 text-right text-[10px] font-bold ${pct >= 95 ? "text-emerald-700" : pct >= 80 ? "text-amber-700" : "text-red-700"}`}>
                      {pct.toFixed(0)}%
                    </span>
                  </div>
                </td>
                {suspended > 0 && (
                  <td className="pr-3 py-2 text-right text-[10px] font-bold text-red-700">
                    {suspended} susp
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  </div>
);
